import React from "react";
import ReactDOM from "react-dom/client";
import {QueryClient, QueryClientProvider} from "react-query";
import {ReactQueryDevtools} from "react-query/devtools";
import {BrowserRouter as Router, Route, Routes} from "react-router-dom";
import App from "./App";
import "./index.css";

import {Auth, Login, Logout, ProtectedRoute} from "./auth";

import OperationalMap from "./pages/OperationalMap/index";

import WorldSearch from "./pages/WorldSearch";
import {PartnerRoutes} from "./routes/PartnerRoutes";


import {
    AdviceGuides,
    AirlineRiskMatrix,
    AllCountryProfiles,
    CountryProfile,
    CountryProfiles,
    GlobalRiskMap,
    Home as RiskHome,
    SecurityAssessments,
} from "./pages/risk";
// Pages --- Trips
import AllTripsPage from "./pages/Trips/AllTripsPage";
import IndividualTripPage from "./pages/Trips/IndividualTrip";
import UpcomingCheckInsPage from "./pages/Trips/UpcomingCheckIns";

// Pages ––- Sites
import {
    SitesAddCustomOverlayPage,
    SitesAddZone,
    SitesCustomOverlayPage,
    SitesEditCustomOverlay,
    SitesEditZone,
    SitesFacilitiesPage,
    SitesHomePage,
    SitesOutlet,
    SitesZonePage,
} from "./pages/Sites";

// Pages --- Settings
import Settings from "./pages/Settings";

import {OidcConfiguration, OidcProvider, OidcSecure, TokenAutomaticRenewMode} from "@axa-fr/react-oidc";

// Pages --- Reports
import ClientsContextProvider from "./context/clients";
import AdviceGuidesEmergencySection from "./features/risk/pages/advice-guides/sections/emergency";
import AdviceGuidesMedicalSection from "./features/risk/pages/advice-guides/sections/medical";
import AdviceGuidesNaturalHazardsSection from "./features/risk/pages/advice-guides/sections/natural-hazards";
import AdviceGuidesTravelSection from "./features/risk/pages/advice-guides/sections/travel";
import CountryProfilesContextProvider from "./features/risk/pages/country-profiles/context/country-profiles";
import CountryProfileSection from "./features/risk/pages/country-profiles/sections/country-profille";
import CountryProfileCrimeSection from "./features/risk/pages/country-profiles/sections/crime";
import CountryProfileCulturalConsiderationSection
    from "./features/risk/pages/country-profiles/sections/cultural-consideration";
import CountryProfileMapSection from "./features/risk/pages/country-profiles/sections/map";
import CountryProfileMedicalSection from "./features/risk/pages/country-profiles/sections/medical";
import CountryProfileNaturalHazardsSection from "./features/risk/pages/country-profiles/sections/natural-hazards";
import CountryProfileOperationalSection from "./features/risk/pages/country-profiles/sections/operational";
import CountryProfileOverviewSection from "./features/risk/pages/country-profiles/sections/overview";
import CountryProfilePoliticalSection from "./features/risk/pages/country-profiles/sections/political";
import CountryProfileRegionalInfoSection from "./features/risk/pages/country-profiles/sections/regional-information";
import CountryProfileSecurityAdviceSection from "./features/risk/pages/country-profiles/sections/security-advice";
import CountryProfileTerrorismSection from "./features/risk/pages/country-profiles/sections/terrorism";
import CountryProfileTravelSection from "./features/risk/pages/country-profiles/sections/travel";
import Reports from "./pages/Reports";
import ReportsDataProvider from "./pages/Reports/ReportsDataProvider";
// import { ErrorBoundary } from "react-error-boundary";
import Authenticating from "./auth/authenticating";
import Error from "./auth/error";
import Loading from "./auth/loading";
import AdviceGuidesContextProvider from "./features/risk/pages/advice-guides/context/advice-guides";
import {CheckInConfiguration} from "./pages/CheckInConfiguration";
import MessageLog from "./pages/Reports/messageProvider";
import CreateNewReportDoc from "./pages/Reports/pages/Summary/components/CreateNewReportDoc";
import DashboardPageNew from "./pages/Reports/pages/Summary/components/DashboardPageNew";
import ReportFileDownload from "./pages/Reports/pages/Summary/components/ReportFileDownload";
import ReportsPage from "./pages/Reports/pages/Summary/components/ReportsPage";
import SummaryPage from "./pages/Reports/pages/Summary/components/SummaryPage";
import IncidentLog from "./pages/RiskV2/IncidentLog";
import AirlineRiskUpload from "./pages/Settings/components/airline-risk-matrix";
import AppInformationUpload from "./pages/Settings/components/app-information-upload";
import {AppCompliance} from "./pages/Settings/components/app-compliance";
import SetBannerAlert from "./pages/Settings/components/banner-alert";
import CustomEmailsRecipients from "./pages/Settings/components/custom-emails-recipients";
import CustomProfiles from "./pages/Settings/pages/CustomProfiles";
import TripCreate from "./pages/TripsV2/create";
import TripEdit from "./pages/TripsV2/edit";
import LocationWatcher from "./util/LocationWatcher";
import ProtectedRouteClient from "./util/ProtectedRouteClient";
import {UserActivationPage} from "./pages/user-activation/UserActivationPage";
import {UserActivationProvider} from "./pages/user-activation/UserActivationContext";
import {PasswordResetProvider} from "./pages/reset-password/PasswordResetContext";
import {ResetPasswordPage} from "./pages/reset-password/ResetPasswordPage";
import {ManagementRoutes} from "./routes/ManagementRoutes";
import {SirenRoutes} from "./routes/SirenRoutes";
import {MassWarningResponsePage} from "./pages/mass-warning-response/MassWarningResponsePage";
import {SirenOptOut} from "./pages/mass-warning-response/SirenOptOut";
import {SirenOptIn} from "./pages/mass-warning-response/SirenOptIn";
import StandardOperatingProcedure from "./pages/Settings/components/standard-operating-procedure";
import {PlatformProvider} from "@/api/PlatformProvider";
import {FacilityRoutes} from "@/routes/FacilityRoutes";
import {SiteAlertRoutes} from "@/routes/SiteAlertRoutes";
import {IncidentFeedRoutes} from "@/routes/IncidentFeedRoutes";
import {IncidentRoutes} from "@/routes/IncidentRoutes";
import {DrawingTestRoutes} from "@/routes/DrawingTestRoutes";
import {CircleTestRoutes} from "@/routes/CircleTestRoutes";
import {WebGLDrawRoutes} from "@/routes/WebGLDrawRoutes";
import {SimpleDrawRoutes} from "@/routes/SimpleDrawRoutes";
import {WebGLMaskDrawRoutes} from "@/routes/WebGLMaskDrawRoutes";
import {MercatorDrawRoutes} from "@/routes/MercatorDrawRoutes";
import {APIKeyRoutes} from "./routes/APIKeyRoutes";


const queryClient = new QueryClient({
    defaultOptions: {queries: {refetchOnWindowFocus: false}},
});

const root = ReactDOM.createRoot(
    document.getElementById("root") as HTMLElement
);


console.log("AUTHORITY:", process.env.REACT_APP_OAUTH2_AUTHORITY)

const configuration: OidcConfiguration = {
    client_id: process.env.REACT_APP_OAUTH2_CLIENT_ID || "",
    redirect_uri: window.location.origin + "/authentication/callback",
    silent_redirect_uri:
        window.location.origin + "/authentication/silent-callback",
    scope: "openid profile email offline_access",
    service_worker_relative_url: "/OidcServiceWorker.js",
    authority: process.env.REACT_APP_OAUTH2_AUTHORITY || "",
    service_worker_only: false,
    token_renew_mode: "access_token_invalid",
    token_automatic_renew_mode: TokenAutomaticRenewMode.AutomaticBeforeTokenExpiration,
    refresh_time_before_tokens_expiration_in_second: 240,
};

// Inside DashboardClientProviderWrapper

const OidcRoutes: React.FC = () => (
    <OidcProvider
        configuration={configuration}
        authenticatingComponent={Loading}
        loadingComponent={Authenticating}
        authenticatingErrorComponent={Error}
    >
        <Routes>
            <Route path="" element={<Auth/>}>
                <Route path="" element={<Login/>}/>
            </Route>
            <Route
                path="logout"
                element={
                    <ProtectedRoute>
                        <Logout/>
                    </ProtectedRoute>
                }
            />

            <Route path="/" element={<App/>}>
                <Route
                    path="operational-map"
                    element={
                        <ProtectedRoute>
                            <ClientsContextProvider>
                                <OperationalMap/>
                            </ClientsContextProvider>
                        </ProtectedRoute>
                    }
                />
                {
                    // temp
                }
                <Route
                    path="world-search"
                    element={
                        <ProtectedRoute>
                            <WorldSearch/>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="risk"
                    element={
                        <ProtectedRoute>
                            <RiskHome/>
                        </ProtectedRoute>
                    }
                >
                    <Route path="global-incident-map" element={<GlobalRiskMap/>}/>

                    <Route
                        path="country-profiles"
                        element={
                            <CountryProfilesContextProvider>
                                <CountryProfiles/>
                            </CountryProfilesContextProvider>
                        }
                    >
                        <Route path="" element={<AllCountryProfiles/>}/>

                        <Route path=":country_iso" element={<CountryProfile/>}>
                            <Route path="" element={<CountryProfileSection/>}>
                                <Route
                                    path="overview"
                                    element={<CountryProfileOverviewSection/>}
                                />
                                <Route
                                    path="political"
                                    element={<CountryProfilePoliticalSection/>}
                                />
                                <Route
                                    path="operational"
                                    element={<CountryProfileOperationalSection/>}
                                />
                                <Route
                                    path="personal-security"
                                    element={<CountryProfileCrimeSection/>}
                                />
                                <Route
                                    path="terrorism"
                                    element={<CountryProfileTerrorismSection/>}
                                />
                                <Route
                                    path="travel"
                                    element={<CountryProfileTravelSection/>}
                                />
                                <Route
                                    path="medical"
                                    element={<CountryProfileMedicalSection/>}
                                />
                                <Route
                                    path="regional-information"
                                    element={<CountryProfileRegionalInfoSection/>}
                                />
                                <Route
                                    path="cultural-considerations"
                                    element={<CountryProfileCulturalConsiderationSection/>}
                                />
                                <Route
                                    path="natural-hazards"
                                    element={<CountryProfileNaturalHazardsSection/>}
                                />
                                <Route
                                    path="security-advice"
                                    element={<CountryProfileSecurityAdviceSection/>}
                                />
                                <Route
                                    path="map"
                                    element={<CountryProfileMapSection/>}
                                />
                            </Route>
                        </Route>
                    </Route>

                    <Route
                        path="advice-guides"
                        element={
                            <AdviceGuidesContextProvider>
                                <AdviceGuides/>
                            </AdviceGuidesContextProvider>
                        }
                    >
                        <Route
                            path="travel"
                            element={<AdviceGuidesTravelSection/>}
                        />
                        <Route
                            path="natural-hazards"
                            element={<AdviceGuidesNaturalHazardsSection/>}
                        />
                        <Route
                            path="medical"
                            element={<AdviceGuidesMedicalSection/>}
                        />
                        <Route
                            path="emergency"
                            element={<AdviceGuidesEmergencySection/>}
                        />
                    </Route>

                    <Route
                        path="incident-log"
                        element={
                            <AdviceGuidesContextProvider>
                                <IncidentLog/>
                            </AdviceGuidesContextProvider>
                        }
                    />

                    <Route
                        path="airline-risk-matrix"
                        element={
                            <ProtectedRouteClient>
                                <AirlineRiskMatrix/>
                            </ProtectedRouteClient>}
                    />

                    <Route
                        path="security-assessments"
                        element={<SecurityAssessments/>}
                    />
                </Route>
                <Route
                    path="settings"
                    element={
                        <ProtectedRoute>
                            <Settings/>
                        </ProtectedRoute>
                    }
                >
                    <Route path="api-keys/*" element={<APIKeyRoutes/>}/>

                    <Route path="country-profiles" element={<CustomProfiles/>}/>
                    <Route
                        path="custom-emails-recipients"
                        element={<CustomEmailsRecipients/>}
                    />
                    <Route
                        path="airline-risk-matrix"
                        element={<AirlineRiskUpload/>}
                    />
                    <Route
                        path="banner-alert"
                        element={<SetBannerAlert/>}
                    />
                    <Route
                        path="app-compliance"
                        element={<AppCompliance/>}
                    />
                    <Route
                        path="standard-operating-procedure"
                        element={<StandardOperatingProcedure/>}
                    />
                    <Route
                        path="app-information-upload"
                        element={<AppInformationUpload/>}
                    />
                    {/* <Route*/}
                    {/*  path="emergency-response"*/}
                    {/*  element={*/}
                    {/*    <UsersDataProvider>*/}
                    {/*      <EmergencyResponse />*/}
                    {/*    </UsersDataProvider>*/}
                    {/*  }*/}
                    {/* >*/}
                    {/*  <Route*/}
                    {/*    path="view-plans"*/}
                    {/*    element={*/}
                    {/*      <UsersDataProvider>*/}
                    {/*        <ViewPlans />*/}
                    {/*      </UsersDataProvider>*/}
                    {/*    }*/}
                    {/*  />*/}
                    {/* </Route>*/}
                    {/* <Route*/}
                    {/*  path="mass-warning"*/}
                    {/*  element={*/}
                    {/*    <UsersDataProvider>*/}
                    {/*      <MassWarning />*/}
                    {/*    </UsersDataProvider>*/}
                    {/*  }*/}
                    {/* >*/}
                    {/*  <Route*/}
                    {/*    path=""*/}
                    {/*    element={*/}
                    {/*      <UsersDataProvider>*/}
                    {/*        <SettingsMassWarningHome />*/}
                    {/*      </UsersDataProvider>*/}
                    {/*    }*/}
                    {/*  />*/}
                    {/*  <Route*/}
                    {/*    path="groups"*/}
                    {/*    element={*/}
                    {/*      <UsersDataProvider>*/}
                    {/*        <AllGroups />*/}
                    {/*      </UsersDataProvider>*/}
                    {/*    }*/}
                    {/*  >*/}
                    {/*    <Route*/}
                    {/*      path=""*/}
                    {/*      element={*/}
                    {/*        <UsersDataProvider>*/}
                    {/*          <Dashboard />*/}
                    {/*        </UsersDataProvider>*/}
                    {/*      }*/}
                    {/*    />*/}
                    {/*    <Route*/}
                    {/*      path="edit-group"*/}
                    {/*      element={*/}
                    {/*        <UsersDataProvider>*/}
                    {/*          <EditGroup />*/}
                    {/*        </UsersDataProvider>*/}
                    {/*      }*/}
                    {/*    />*/}
                    {/*  </Route>*/}
                    {/*  <Route*/}
                    {/*    path="all-email-templates"*/}
                    {/*    element={*/}
                    {/*      <UsersDataProvider>*/}
                    {/*        <SeeAllEmailTemplates />*/}
                    {/*      </UsersDataProvider>*/}
                    {/*    }*/}
                    {/*  />*/}
                    {/*  <Route*/}
                    {/*    path="all-audio-files"*/}
                    {/*    element={*/}
                    {/*      <UsersDataProvider>*/}
                    {/*        <SeeAllAudioFiles />*/}
                    {/*      </UsersDataProvider>*/}
                    {/*    }*/}
                    {/*  />*/}
                    {/* </Route>*/}
                    {/* <Route*/}
                    {/*  path="custom-emails"*/}
                    {/*  element={*/}
                    {/*    <UsersDataProvider>*/}
                    {/*      <CustomEmails />*/}
                    {/*    </UsersDataProvider>*/}
                    {/*  }*/}
                    {/* >*/}
                    {/*  <Route*/}
                    {/*    path="invitation-email"*/}
                    {/*    element={*/}
                    {/*      <UsersDataProvider>*/}
                    {/*        <InvitationEmail />*/}
                    {/*      </UsersDataProvider>*/}
                    {/*    }*/}
                    {/*  />*/}
                    {/*  <Route*/}
                    {/*    path="pre-trip-email"*/}
                    {/*    element={*/}
                    {/*      <UsersDataProvider>*/}
                    {/*        <PreTripEmail />*/}
                    {/*      </UsersDataProvider>*/}
                    {/*    }*/}
                    {/*  />*/}
                    {/*  <Route*/}
                    {/*    path="new-trip-email"*/}
                    {/*    element={*/}
                    {/*      <UsersDataProvider>*/}
                    {/*        <NewTripEmail />*/}
                    {/*      </UsersDataProvider>*/}
                    {/*    }*/}
                    {/*  />*/}
                    {/*  <Route*/}
                    {/*    path="real-time-travellers-affected"*/}
                    {/*    element={*/}
                    {/*      <UsersDataProvider>*/}
                    {/*        <RealTimeTravellersAffected />*/}
                    {/*      </UsersDataProvider>*/}
                    {/*    }*/}
                    {/*  />*/}
                    {/*  <Route*/}
                    {/*    path="compliance-report-email"*/}
                    {/*    element={*/}
                    {/*      <UsersDataProvider>*/}
                    {/*        <ComplianceReportEmail />*/}
                    {/*      </UsersDataProvider>*/}
                    {/*    }*/}
                    {/*  />*/}
                    {/* </Route>*/}
                    {/* <Route*/}
                    {/*  path="facilities"*/}
                    {/*  element={*/}
                    {/*    <UsersDataProvider>*/}
                    {/*      <Facilities />*/}
                    {/*    </UsersDataProvider>*/}
                    {/*  }*/}
                    {/* />*/}
                </Route>
                <Route
                    path="sites"
                    element={
                        <ProtectedRoute>
                            <SitesOutlet/>
                        </ProtectedRoute>
                    }
                >
                    <Route
                        path=":client_id?/:facility_id?"
                        element={<SitesHomePage/>}
                    />
                    <Route
                        path="custom-overlay"
                        element={<SitesCustomOverlayPage/>}
                    />
                    <Route
                        path="add-custom-overlay"
                        element={<SitesAddCustomOverlayPage/>}
                    />
                    <Route
                        path="edit-custom-overlay/:id"
                        element={<SitesEditCustomOverlay/>}
                    />
                    <Route path="zones" element={<SitesZonePage/>}/>
                    <Route path="facilities" element={<SitesFacilitiesPage/>}/>
                    <Route path="add-zone" element={<SitesAddZone/>}/>
                    <Route
                        path="edit-zone/:type/:zoneID"
                        element={<SitesEditZone/>}
                    />
                </Route>
                <Route
                    path="trips"
                    element={
                        <ProtectedRoute>
                            <AllTripsPage/>
                        </ProtectedRoute>
                    }
                />

                <Route
                    path="trips/check-in-configuration"
                    element={
                        <ProtectedRoute>
                            <CheckInConfiguration/>

                        </ProtectedRoute>
                    }
                />

                <Route
                    path="trips/:id"
                    element={
                        <ProtectedRoute>
                            <IndividualTripPage/>
                        </ProtectedRoute>
                    }
                />


                <Route
                    path="trips/create"
                    element={
                        <ProtectedRoute>
                            <TripCreate/>
                        </ProtectedRoute>
                    }
                />

                <Route
                    path="trips/:id/edit"
                    element={
                        <ProtectedRoute>
                            <TripEdit/>
                        </ProtectedRoute>
                    }
                />

                <Route
                    path="trips/upcoming-check-ins"
                    element={
                        <ProtectedRoute>
                            <UpcomingCheckInsPage/>
                        </ProtectedRoute>
                    }
                />


                <Route path="management/*" element={<ManagementRoutes/>}/>
                <Route path="siren/*" element={<SirenRoutes/>}/>
                <Route path="facilities/*" element={<OidcSecure><FacilityRoutes/></OidcSecure>}/>
                <Route path="site-alerts/*" element={<OidcSecure><SiteAlertRoutes/></OidcSecure>}/>
                <Route path="incident-feeds/*" element={<IncidentFeedRoutes/>}/>
                <Route path="incidents/*" element={<IncidentRoutes/>}/>
                <Route path="drawing-test/*" element={<DrawingTestRoutes/>}/>
                <Route path="circle-test/*" element={<CircleTestRoutes/>}/>
                <Route path="webgl-draw/*" element={<WebGLDrawRoutes/>}/>
                <Route path="simple-draw/*" element={<SimpleDrawRoutes/>}/>
                <Route path="webgl-mask-draw/*" element={<WebGLMaskDrawRoutes/>}/>
                <Route path="mercator-draw/*" element={<MercatorDrawRoutes/>}/>

                <Route
                    path="reporting"
                    element={
                        <ProtectedRoute>
                            {/* <DashboardClientProviderWrapper> */}
                            <ReportsDataProvider>
                                <Reports/>
                            </ReportsDataProvider>
                            {/* </DashboardClientProviderWrapper> */}
                        </ProtectedRoute>
                    }
                >

                    {/* <Route path="summary" element={<DashboardClientProviderPage />}>
                      <Route index element={<DashboardPage />} />
                      <Route path=":dashboardID" element={<DashboardPage />} />
                    </Route> */}

                    <Route
                        path="summary/:dashboardID?"
                        element={
                            <DashboardPageNew/>
                        }

                    />


                    <Route path="create-new-report" element={<CreateNewReportDoc/>}/>

                    <Route path="reports" element={<ReportsPage/>}>
                        <Route path="summary" element={<SummaryPage/>}/>
                        <Route path="saved-reports" element={<CreateNewReportDoc/>}/>
                    </Route>

                    <Route path="messages" element={<MessageLog/>}/>
                </Route>

                <Route
                    path="partners/*"
                    element={
                        <ProtectedRoute>
                            <PartnerRoutes/>
                        </ProtectedRoute>
                    }
                />

            </Route>
            <Route path="reports/download" element={<ReportFileDownload/>}/>
            <Route path="messages" element={<MessageLog/>}/>
        </Routes>
    </OidcProvider>
);

root.render(
    <React.StrictMode>
        <QueryClientProvider client={queryClient}>
            <PlatformProvider>
            <Router>
                <LocationWatcher/>
                <Routes>
                    <Route path="mass-warning-response" element={<MassWarningResponsePage/>}/>
                    <Route path="mass-warning-response/:id" element={<MassWarningResponsePage/>}/>
                    <Route path="siren-opt-out/:id" element={<SirenOptOut/>}/>
                    <Route path="siren-opt-in" element={<SirenOptIn/>}/>
                    <Route path="activate-account/:token"
                           element={<UserActivationProvider><UserActivationPage/></UserActivationProvider>}/>
                    <Route path="reset-password/:token"
                           element={<PasswordResetProvider><ResetPasswordPage/></PasswordResetProvider>}/>
                    <Route path="/*" element={<OidcRoutes/>}/>
                </Routes>
            </Router>
            <ReactQueryDevtools initialIsOpen={false}/>
            </PlatformProvider>
        </QueryClientProvider>
    </React.StrictMode>
);
